import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, CalendarClockIcon, GraduationCapIcon, HomeIcon, SearchXIcon, UsersIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ProvenanceBadge } from '../../components/trust/ProvenanceBadge';
import { programmes, scholarships, universityProfileById } from '../../data/admissions';
import { accommodationPressure } from '../../data/institution';
import { verifiableInstitutions } from '../../data/verification';
import { tuitionLabel } from '../../components/discover/CompareTray';

/**
 * One institution, in full.
 *
 * The organising question is "should I apply here" — so what leads is what
 * differs between institutions and what is actually established about this one,
 * not a wall of equal-weight statistics.
 */
export function UniversityDetail() {
  const {
    universityId = ''
  } = useParams();
  const navigate = useNavigate();
  const university = universityProfileById(universityId);
  if (!university) {
    return <Panel data-id="element-11644" data-mp="1km2rokp1f2r">
        <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-11646" data-mp="1jkpddg7gghv" />} title="Institution not found" description="This institution is not on Campuzon, or its page has been withdrawn." action={<Button variant="secondary" onClick={() => navigate('/discover')} data-id="element-11647" data-mp="bansslwlx7kb">
              Back to Discover
            </Button>} data-id="element-11645" data-mp="1k2xp8g56l65" />
      </Panel>;
  }
  const ourProgrammes = programmes.filter(entry => entry.universityId === university.id);
  const linkedScholarshipIds = new Set(ourProgrammes.flatMap(entry => entry.scholarshipIds));
  const linkedScholarships = scholarships.filter(entry => linkedScholarshipIds.has(entry.id));

  // Verification capability is a property of the institution, and it decides
  // what a student here can ever prove. Worth saying on the institution page.
  const verifiable = verifiableInstitutions.find(entry => entry.name === university.name || entry.shortName === university.shortName);
  return <div className="space-y-6" data-id="element-11648" data-mp="140oawa2pm3d1">
      <Link to="/discover" className="inline-flex items-center gap-1.5 text-body-sm font-medium text-ink-muted hover:text-ink" data-id="element-11649" data-mp="140y406j1wqmy">
        <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-11650" data-mp="1dx9dypv654e8" />
        Discover
      </Link>

      <PageHeader eyebrow={`${university.city}, ${university.state} · ${university.country}`} title={university.name} description={university.representative ? `Published by ${university.representative}.` : 'No verified institution representative has claimed this page. Details were entered by students and are not confirmed by the university.'} actions={<div className="flex flex-wrap items-center gap-2" data-id="element-11652" data-mp="3h74espf9diek">
            <Badge plain data-id="element-11653" data-mp="3c7i00bjs7u3n">{university.type}</Badge>
            <ProvenanceBadge provenance={university.provenance} data-id="element-11654" data-mp="3c5oux0othhxr" />
          </div>} data-id="element-11651" data-mp="1iorj7q485r50" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-11655" data-mp="3866jqwmssl84">
        <div className="space-y-5" data-id="element-11656" data-mp="337bt58il9sdg">
          <Panel title={`Programmes on Campuzon (${ourProgrammes.length})`} description="Only programmes with a published record appear here. The institution may run others." bodyClassName="p-0" data-id="element-11657" data-mp="338ei3awcpkqq">
            {ourProgrammes.length === 0 ? <div className="px-5 py-4 sm:px-6" data-id="element-11658" data-mp="3r2y1v2qlsual">
                <p className="max-w-[74ch] text-body-sm text-ink" data-id="element-11659" data-mp="3m5nzynm12q5v">
                  No programmes are published for this institution yet. That means nobody has supplied
                  them — not that the institution has none.
                </p>
              </div> : <ul className="divide-y divide-line" data-id="element-11660" data-mp="296hsxnxo242">
                {ourProgrammes.map(programme => <li key={programme.id} className="px-5 py-4 sm:px-6" data-id="element-11661" data-mp="7819yq7sj30u">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2.5" data-id="element-11662" data-mp="766nh7gvf2un">
                      <div className="min-w-0" data-id="element-11663" data-mp="3pfz5cj3gbu2l">
                        <Link to={`/discover/programmes/${programme.id}`} className="text-body-lg font-semibold text-ink hover:underline" data-id="element-11664" data-mp="3uc7f1gyvqv2d">
                          {programme.title}
                        </Link>
                        <p className="mt-0.5 flex flex-wrap items-center gap-2" data-id="element-11665" data-mp="c7vxcjscxah5">
                          <Badge plain data-id="element-11666" data-mp="h6795027pv20">{programme.level}</Badge>
                          <Badge plain data-id="element-11667" data-mp="h4mhpjb72s8d">{programme.deliveryMode}</Badge>
                        </p>
                        <p className="cz-num mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-body-sm text-ink-muted" data-id="element-11668" data-mp="14uqizk0wufw7">
                          <span data-id="element-11669" data-mp="1j1k0apm3t8qz">{tuitionLabel(programme)}</span>
                          <span data-id="element-11670" data-mp="1j00har92cjli">{programme.durationMonths} months</span>
                          <span className="inline-flex items-center gap-1.5" data-id="element-11671" data-mp="1eyx64i1ynbqm">
                            <CalendarClockIcon aria-hidden className="h-4 w-4" data-id="element-11672" data-mp="1srwkggq0azwl" />
                            {programme.deadlineLabel}
                          </span>
                        </p>
                      </div>
                      <Link to={`/discover/programmes/${programme.id}`} data-id="element-11673" data-mp="103qwll3nousj">
                        <Button size="sm" variant="secondary" data-id="element-11674" data-mp="w1dvxb3rnn2m">
                          Open
                        </Button>
                      </Link>
                    </div>
                  </li>)}
              </ul>}
          </Panel>

          <Panel title="Funding linked to these programmes" bodyClassName="p-0" data-id="element-11675" data-mp="153vdvdce1tjt">
            {linkedScholarships.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted sm:px-6" data-id="element-11676" data-mp="2ghh3ezm5tx5v">
                No funding is linked to these programmes on Campuzon.
              </p> : <ul className="divide-y divide-line" data-id="element-11677" data-mp="2sj8n7r4b5349">
                {linkedScholarships.map(scholarship => <li key={scholarship.id} className="px-5 py-4 sm:px-6" data-id="element-11678" data-mp="2xhtprx717yvv">
                    <Link to={`/discover/scholarships/${scholarship.id}`} className="text-body font-semibold text-ink hover:underline" data-id="element-11679" data-mp="2xginy2x5d17e">
                      {scholarship.name}
                    </Link>
                    <p className="cz-num mt-0.5 text-body-sm text-ink-muted" data-id="element-11680" data-mp="29taed8sor5zk">
                      {scholarship.amountLabel} · {scholarship.funder} · {scholarship.deadlineLabel}
                    </p>
                  </li>)}
              </ul>}
          </Panel>

          <Panel title="Living here" description="Housing pressure around this campus, from listings and applications on Campuzon." bodyClassName="p-0" data-id="element-11681" data-mp="1m2n78pqlgke2">
            <div className="overflow-x-auto" data-id="element-11682" data-mp="1ji8epswhw06h">
              <table className="w-full min-w-[34rem] text-left" data-id="element-11683" data-mp="1ejx43fydlln7">
                <thead data-id="element-11684" data-mp="1ekf6kubmsw3k">
                  <tr className="border-b border-line" data-id="element-11685" data-mp="19kkiszn5kmmq">
                    <th className="px-5 py-3 text-body-sm font-semibold text-ink-muted sm:px-6" data-id="element-11686" data-mp="19izn36p3qxna">
                      Area
                    </th>
                    <th className="px-5 py-3 text-body-sm font-semibold text-ink-muted" data-id="element-11687" data-mp="10jmcgap8s30z">Demand</th>
                    <th className="px-5 py-3 text-body-sm font-semibold text-ink-muted" data-id="element-11688" data-mp="vtfq6hi7culw">
                      Listed beds
                    </th>
                    <th className="px-5 py-3 text-body-sm font-semibold text-ink-muted" data-id="element-11689" data-mp="qukxmnufnrol">
                      Median rent
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line" data-id="element-11690" data-mp="gulput5u85cs">
                  {accommodationPressure.map(row => <tr key={row.neighbourhood} data-id="element-11691" data-mp="cv3fusfk1eoq">
                      <td className="px-5 py-3 text-body-sm text-ink sm:px-6" data-id="element-11692" data-mp="20qgdyd0zmwe6">
                        {row.neighbourhood}
                      </td>
                      <td className="cz-num px-5 py-3 text-body-sm text-ink" data-id="element-11693" data-mp="25pb5q4gxnvaf">{row.demand}</td>
                      <td className="cz-num px-5 py-3 text-body-sm text-ink" data-id="element-11694" data-mp="1qsafk5ee48e2">{row.listedBeds}</td>
                      <td className="cz-num px-5 py-3 text-body-sm text-ink" data-id="element-11695" data-mp="1vqbv4gclxpks">{row.medianRent}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="border-t border-line px-5 py-4 sm:px-6" data-id="element-11696" data-mp="1msjen8v5gbvt">
              <Link to="/housing" data-id="element-11697" data-mp="183di5snp4zrm">
                <Button size="sm" variant="secondary" icon={<HomeIcon className="h-4 w-4" data-id="element-11699" data-mp="1d0vrvl9jfj0o" />} data-id="element-11698" data-mp="1845amulzyhdo">
                  Search housing near this campus
                </Button>
              </Link>
            </div>
          </Panel>
        </div>

        <div className="space-y-5" data-id="element-11700" data-mp="132zgqtgloo2k">
          <Panel title="At a glance" data-id="element-11701" data-mp="131xxw2tcexaa">
            <dl className="space-y-3" data-id="element-11702" data-mp="13526rdhr6h7a">
              <Row icon={<UsersIcon className="h-4 w-4" data-id="element-11704" data-mp="kfgwu8847e81" />} label="Students" value={university.students.toLocaleString()} data-id="element-11703" data-mp="ke5l9m8fh26d" />
              <Row icon={<BuildingIcon className="h-4 w-4" data-id="element-11706" data-mp="tgeso38egffj" />} label="Founded" value={String(university.founded)} data-id="element-11705" data-mp="tg52aejg069b" />
              <Row icon={<GraduationCapIcon className="h-4 w-4" data-id="element-11708" data-mp="13czy2pobzsjw" />} label="Scholarships listed" value={String(university.scholarshipsAvailable)} data-id="element-11707" data-mp="13dt942ubp47u" />
            </dl>
            <ul className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4" data-id="element-11709" data-mp="1c6mtgeommtsk">
              {university.internationalIntake && <li data-id="element-11710" data-mp="185kqbmrl9xlb"><Badge tone="brand" data-id="element-11711" data-mp="185t2fcfw0yvg">International intake</Badge></li>}
              {university.accommodationOnCampus && <li data-id="element-11712" data-mp="1m4kcmnrmq0eb">
                  <Badge plain data-id="element-11713" data-mp="1m1ya52jlib2y">On-campus housing</Badge>
                </li>}
            </ul>
          </Panel>

          <Panel title="Campuses" bodyClassName="p-0" data-id="element-11714" data-mp="139h8d0vzb8af">
            <ul className="divide-y divide-line" data-id="element-11715" data-mp="13b21cm2naj66">
              {university.campuses.map(campus => <li key={campus} className="px-5 py-3 text-body-sm text-ink sm:px-6" data-id="element-11716" data-mp="yg4m2xervdku">
                  {campus}
                </li>)}
            </ul>
          </Panel>

          <Panel title="What you could prove here" data-id="element-11717" data-mp="pjdr6obyi4ru">
            {verifiable ? <p className="max-w-[60ch] text-body-sm text-ink" data-id="element-11718" data-mp="1m6igguktxoil">
                Students at {verifiable.shortName} can reach{' '}
                <span className="font-medium" data-id="element-11719" data-mp="1i4dmfuqgypvf">
                  {verifiable.maxLevelSupported.replace(/_/g, ' ')}
                </span>
                . That decides which restricted communities, roommate matching and seller features are
                open to you here.
              </p> : <p className="max-w-[60ch] text-body-sm text-ink" data-id="element-11720" data-mp="3sy9ezwnnj3t5">
                Campuzon has no verification arrangement with this institution yet, so a student here
                cannot currently be confirmed as enrolled. Document review remains available.
              </p>}
            <Link to="/identity" className="mt-3 inline-block" data-id="element-11721" data-mp="3nr2wd53sz2nq">
              <Button size="sm" variant="secondary" data-id="element-11722" data-mp="3sqxotd3880aw">
                How verification works
              </Button>
            </Link>
          </Panel>

          <Panel title="Help applying" data-id="element-11723" data-mp="5fokvc2tlr6f">
            <p className="max-w-[60ch] text-body-sm text-ink" data-id="element-11724" data-mp="5emywck9nk9x">
              Independent providers in the Campuzon marketplace offer statement review, references and
              interview preparation. Campuzon supplies the marketplace — never the admission decision.
            </p>
            <Link to="/marketplace" className="mt-3 inline-block" data-id="element-11725" data-mp="kbnr0h6ddh8x">
              <Button size="sm" variant="ghost" data-id="element-11726" data-mp="2h4z33tl40bgb">
                Browse providers
              </Button>
            </Link>
          </Panel>
        </div>
      </div>
    </div>;
}
function Row({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-id="element-11727" data-mp="294axzl99wvhk">
      <dt className="inline-flex items-center gap-2 text-body-sm text-ink-muted" data-id="element-11728" data-mp="296nlsf1pq02j">
        <span aria-hidden className="text-ink-subtle" data-id="element-11729" data-mp="2o2v6dgh193ic">
          {icon}
        </span>
        {label}
      </dt>
      <dd className="cz-num text-body font-medium text-ink" data-id="element-11730" data-mp="2j4tnavjlx46v">{value}</dd>
    </div>;
}